import { useRef, useState } from 'react'
import css from 'classnames'
import { XYCoord, useDrag, useDrop } from 'react-dnd'
import { Link } from 'react-router-dom'
import { NestedCategories } from '~/shared/api'
import { moveElement, replaceCategoriesById } from '../../lib/helpers'
import { DropZone } from './drop-zone'
import { SubItem2 } from './sub-item-2'
import { CategoryStatusIndicators } from './category-status-indicators'
import { SVGDragIndicator, ArrowToDown, SVGPencil } from '~/shared/ui'

interface Props {
  data: NestedCategories
  subList: NestedCategories[]
  list: NestedCategories[]
  index: number
  parentItem: NestedCategories
  setList: (data: NestedCategories[]) => void
  updateList: (draggingId: string, targetItem: NestedCategories) => void
  updated: () => void
}

export const SubItem = ({
  data,
  index,
  subList,
  parentItem,
  setList,
  list,
  updateList,
  updated,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLDivElement | null>(null)
  const [expanded, setExpanded] = useState(false)

  const [{ isOverCurrent }, drop] = useDrop({
    accept: 'sub-category',
    hover: (item: NestedCategories & { index: number }, monitor) => {
      if (!headerRef.current) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const isOverShallow = monitor.isOver({ shallow: true })

      if (!isOverShallow) return

      const hoveredRect = headerRef.current.getBoundingClientRect()
      const mousePosition = monitor.getClientOffset() as XYCoord | null

      if (!mousePosition) return

      const hoverClientY = mousePosition.y - hoveredRect.top
      const hoverHeight = hoveredRect.bottom - hoveredRect.top
      const hoverStartY = hoverHeight * 0.35
      const hoverEndY = hoverHeight * 0.65

      if (dragIndex < hoverIndex && hoverClientY < hoverStartY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverEndY) return

      const newData = moveElement(subList, dragIndex, hoverIndex)

      const newList = replaceCategoriesById(list, parentItem.id, newData)

      setList(newList)
      updated()

      item.index = hoverIndex
    },

    collect: (monitor) => ({
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'sub-category',
    item: { ...data, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const isHighlighted = isOverCurrent && !isDragging

  drag(ref)
  preview(headerRef)
  drop(dragPreviewRef)

  return (
    <div
      ref={dragPreviewRef}
      className={css(
        'sub-category w-full min-w-0 overflow-hidden rounded-lg border border-[#E0E0E0] bg-gray-100',
        {
          'h-auto': expanded,
          'h-[56px]': !expanded,
          'opacity-50': isDragging,
          'bg-[#EFF6FF] shadow-[0_0_0_1px_rgba(59,130,246,0.15)]':
            isHighlighted,
        },
      )}
    >
      <div
        className="flex h-[56px] items-center justify-between gap-3"
        ref={headerRef}
      >
        <span
          ref={ref}
          className="flex cursor-grab item-center shrink-0 justify-center p-3 active:cursor-grabbing"
        >
          <SVGDragIndicator />
        </span>
        <div className="flex min-w-0 flex-1 items-center">
          <h2
            className={css('min-w-0 flex-1 truncate text-[18px] font-medium', {
              'opacity-50': !data.showInMenu,
            })}
          >
            {data.name}
          </h2>
        </div>

        <div className="ml-3 flex shrink-0 items-center gap-3">
          <CategoryStatusIndicators
            enabled={data.enabled}
            showInMenu={data.showInMenu}
          />

          <Link
            to={`/categories/${data.id}/edit`}
            className="group flex shrink-0 items-center justify-center"
          >
            <div
              className={css('text-gray-400', {
                'opacity-50': !data.showInMenu,
              })}
            >
              <SVGPencil />
            </div>
          </Link>
        </div>
        <div
          className="mr-3 flex h-[56px] w-10 shrink-0 cursor-pointer items-center justify-center"
          onClick={() => setExpanded(!expanded)}
        >
          <ArrowToDown
            className={css('transition-all duration-75', {
              'rotate-180': expanded,
            })}
          />
        </div>
      </div>

      <div className="px-3 pb-3">
        <hr />

        <div className="mt-3 mb-3 flex flex-col gap-3">
          {data?.categories?.map((sub, idx) => (
            <SubItem2
              key={sub?.id}
              data={sub}
              list={list}
              updateList={updateList}
              subList={data.categories || []}
              index={idx}
              setList={setList}
              parentItem={data}
              updated={updated}
            />
          ))}
        </div>

        <div className="mt-3 flex h-[100px] min-w-[350px] w-full flex-1">
          <DropZone updateList={updateList} parentElement={data} />
        </div>
      </div>
    </div>
  )
}
